import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getPurchaseBatches, getItems } from '../inventoryApi';
import {
  GlobalKeyframes, PageHeader, PrimaryButton, useToasts, ToastStack,
} from './InventoryUIKit';
import PurchaseSummaryCards from './PurchaseSummaryCards';
import PurchaseTableToolbar from './PurchaseTableToolbar';
import PurchaseBatchTable from './PurchaseBatchTable';
import PurchaseBatchModal from './PurchaseBatchModal';
import PurchaseBatchDetailsModal from './PurchaseBatchDetailsModal';

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const getStatusFromBatch = (batch) => {
  if (batch.is_exhausted || Number(batch.quantity_remaining) <= 0) return 'Exhausted';
  const purchased = Number(batch.purchase_quantity);
  const remaining = Number(batch.quantity_remaining);
  if (purchased <= 0) return 'Exhausted';
  const remainingPct = (remaining / purchased) * 100;
  if (remainingPct > 50) return 'Healthy';
  if (remainingPct > 20) return 'Low Stock';
  return 'Critical';
};

const PurchaseBatchManagement = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [inventoryItems, setInventoryItems] = useState([]);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    supplier: '',
    status: '',
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showAddModal, setShowAddModal] = useState(false);
  const [viewingBatchId, setViewingBatchId] = useState(null);
  const { toasts, pushToast, removeToast } = useToasts();

  const fetchInventoryItems = useCallback(async () => {
    try {
      const data = await getItems({ is_active: true });
      setInventoryItems(normalizeList(data));
    } catch {
      setInventoryItems([]);
    }
  }, []);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPurchaseBatches();
      setBatches(normalizeList(data));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load purchase batches.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInventoryItems(); }, [fetchInventoryItems]);
  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const supplierOptions = useMemo(() => {
    const set = new Set(batches.map((b) => b.supplier).filter(Boolean));
    return Array.from(set).sort();
  }, [batches]);

  const filteredBatches = useMemo(() => {
    let result = batches;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((b) => {
        const itemName = (b.inventory_item_name || '').toLowerCase();
        const itemCode = (b.inventory_item_code || '').toLowerCase();
        return (
          (b.invoice_number || '').toLowerCase().includes(q) ||
          (b.supplier || '').toLowerCase().includes(q) ||
          itemName.includes(q) ||
          itemCode.includes(q)
        );
      });
    }

    if (filters.dateFrom) {
      result = result.filter((b) => b.purchase_date >= filters.dateFrom);
    }
    if (filters.dateTo) {
      result = result.filter((b) => b.purchase_date <= filters.dateTo);
    }
    if (filters.supplier) {
      result = result.filter((b) => b.supplier === filters.supplier);
    }
    if (filters.status) {
      result = result.filter((b) => getStatusFromBatch(b) === filters.status);
    }

    return result;
  }, [batches, search, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredBatches.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredBatches.slice(start, start + pageSize);
  }, [filteredBatches, safePage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, filters, pageSize]);

  const handleSearchChange = useCallback((val) => {
    setSearch(val);
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setFilters({ dateFrom: '', dateTo: '', supplier: '', status: '' });
  }, []);

  const handleAddSuccess = useCallback((created) => {
    setShowAddModal(false);
    fetchBatches();
    pushToast(
      `Purchase batch ${created?.invoice_number ? `"${created.invoice_number}"` : ''} added successfully.`,
      'success'
    );
  }, [fetchBatches, pushToast]);

  const handleView = useCallback((batch) => {
    setViewingBatchId(batch.id);
  }, []);

  const handleEdit = useCallback((batch) => {
    setViewingBatchId(batch.id);
  }, []);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      <GlobalKeyframes />
      <ToastStack toasts={toasts} removeToast={removeToast} />

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @media (max-width: 1023px) {
          .pb-hide-tablet { display: none !important; }
        }
        @media (max-width: 767px) {
          .pb-mobile-card table, .pb-mobile-card thead, .pb-mobile-card tbody,
          .pb-mobile-card th, .pb-mobile-card td, .pb-mobile-card tr {
            display: block;
          }
          .pb-mobile-card thead { display: none; }
          .pb-mobile-card table { width: 100%; }
          .pb-mobile-card tbody tr {
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            margin-bottom: 12px;
            padding: 12px;
            background: #ffffff;
            box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          }
          .pb-mobile-card tbody td {
            padding: 4px 0;
            text-align: left;
            border: none;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 13px;
          }
          .pb-mobile-card tbody td::before {
            content: attr(data-label);
            font-weight: 600;
            color: #6b7280;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.03em;
          }
        }
      `}</style>

      <PageHeader
        title="Purchase Batch Management"
        description="Track procurement batches and FIFO stock value"
        onRefresh={fetchBatches}
        refreshing={loading}
      >
        <PrimaryButton onClick={() => setShowAddModal(true)}>+ Add Purchase Batch</PrimaryButton>
      </PageHeader>

      <PurchaseSummaryCards batches={batches} loading={loading} error={error} />

      <PurchaseTableToolbar
        search={search}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClear={handleClearFilters}
        supplierOptions={supplierOptions}
      />

      <div className="pb-mobile-card">
        <PurchaseBatchTable
          batches={pageItems}
          loading={loading}
          error={error}
          onRetry={fetchBatches}
          onView={handleView}
          onEdit={handleEdit}
          page={safePage}
          setPage={setPage}
          totalPages={totalPages}
          totalItems={filteredBatches.length}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </div>

      {showAddModal && (
        <PurchaseBatchModal
          inventoryItems={inventoryItems}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {viewingBatchId && (
        <PurchaseBatchDetailsModal
          batchId={viewingBatchId}
          onClose={() => setViewingBatchId(null)}
        />
      )}
    </div>
  );
};

export default PurchaseBatchManagement;
